<template>
    <div class="home" v-if="doneLoading && opts.attachmentTypeOptions && opts.facilityTypeOptions">
        <b-container fluid>
            <b-row>
                <b-col>
                    <h1>Application Resubmission</h1>
                </b-col>
            </b-row>

            <div class="card">
                <div class="card-header">Personal Information</div>
                <div class="card-body">
                    <div v-if="corrections.personalInfoCorrections.reasons || hasFlaggedPersonalInfoFields" class="alert-danger p-2 mb-2">
                        <div>
                            <b-row>
                                <b-col class="font-weight-bold">
                                    MDH admin who rejected your application has provided the following reasons regarding your personal information:
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    {{corrections.personalInfoCorrections.reasons}}
                                </b-col>
                            </b-row>
                        </div>
                        <b-row v-if="hasFlaggedPersonalInfoFields">
                            <b-col class="font-weight-bold">
                                Fields with issues have been highlighted in red.
                            </b-col>
                        </b-row>
                    </div>
                    <b-row>
                        <b-col cols="4">
                            <BaseTextOutput label="Last Name:" :value="profile.lastName"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="Middle Name:" :value="profile.middleName"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="First Name:" :value="profile.firstName"/>
                        </b-col>
                        <b-col cols="2">
                            <b-button variant="primary" class="mr-4" @click="showNameModal">Update Name</b-button>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="5">
                            <BaseTextOutput label="Social Security Number:" :value="profile.ssn"/>
                        </b-col>
                        <b-col cols="5">
                            <BaseTextOutput label="Date of Birth:" :value="profile.dateOfBirth"/>
                        </b-col>
                        <b-col cols="2">
                            <b-button variant="primary" class="mr-4" @click="showSsnModal">Shown SSN is incorrect</b-button>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="6" v-bind:class="corrections.personalInfoCorrections.address.streetAddress?'is-invalid':''">
                            <BaseTextInput v-model="profile.contact.address.streetAddress"
                                           :v="$v.profile.contact.address.streetAddress"
                                           slug="streetAddress"
                                           label="Street Address"
                                           setId="applStreetAddress"
                                           tip="Please include apartment number or PO Box number if relevant."
                                           @input="corrections.personalInfoCorrections.address.streetAddress=false"
                            />
                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.personalInfoCorrections.address.city?'is-invalid':''">
                            <BaseTextInput v-model="profile.contact.address.city"
                                           :v="$v.profile.contact.address.city"
                                           slug="city"
                                           label="City"
                                           setId="applCity"
                                           @input="corrections.personalInfoCorrections.address.city=false"
                            />
                        </b-col>
                        <b-col cols="12" lg="4" v-bind:class="corrections.personalInfoCorrections.address.state?'is-invalid':''">
                            <BaseSelector v-model="profile.contact.address.state"
                                          :v="$v.profile.contact.address.state"
                                          slug="state"
                                          label="State"
                                          setId="applState"
                                          :options="opts.stateOptions"
                                          @input="firstSelectorUpdateCatch?corrections.personalInfoCorrections.address.state=false:firstSelectorUpdateCatch=true"
                            />
                        </b-col>
                        <b-col cols="12" lg="3" v-bind:class="corrections.personalInfoCorrections.address.postalCode?'is-invalid':''">
                            <BaseMaskedTextInput v-model="profile.contact.address.postalCode"
                                                 :v="$v.profile.contact.address.postalCode"
                                                 slug="postalCode"
                                                 label="ZIP"
                                                 setId="applZip"
                                                 mask="#####-####"
                                                 @input="corrections.personalInfoCorrections.address.postalCode=false"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12" lg="4" v-bind:class="corrections.personalInfoCorrections.phone?'is-invalid':''">
                            <BaseMaskedTextInput v-model="profile.contact.phone"
                                                 :v="$v.profile.contact.phone"
                                                 slug="phone"
                                                 label="Cell Phone Number"
                                                 setId="applPhone"
                                                 mask="(###)###-####"
                                                 @input="corrections.personalInfoCorrections.phone=false"
                            />
                        </b-col>
                        <b-col cols="12" lg="4" v-bind:class="corrections.personalInfoCorrections.email?'is-invalid':''">
                            <BaseTextInput v-model="profile.contact.email"
                                           :v="$v.profile.contact.email"
                                           slug="email"
                                           label="Email Address"
                                           setId="applEmailAddress"
                                           @input="corrections.personalInfoCorrections.email=false"
                            />
                        </b-col>
                    </b-row>
                </div>
            </div>
           <div>

             <div v-if="lastWorkedDateFromPreviousApplication && overTwoYears">
               <div>You have a gap longer than 2 years since the employment provided on your previous renewal application or your test out, whichever is later.<br/>
                 Depending on your specific work history, you might need to provide W-2(s), first paystub, most recent paystub, or any combination thereof.</div>
               <div v-if="gaps.length>0">
                 <div class="font-weight-bold">Uncovered period{{gaps.length>1?'s':''}}:</div>
                 <ul>
                   <li v-for="(gap, index) in gaps" :key="index">{{gap.start}} - {{gap.end}}</li>
                 </ul>
                 <div class="alert alert-danger">If you have a gap over 24 months in nurse aide work history, you must retest and pass both the knowledge and skills test to become Active on the Minnesota Nurse Aide Registry.</div>
               </div>
               <div class="alert alert-info">We recommend filling out the employment history fully prior to attaching any documents as documents requested per employer may change depending on your specific work history.</div>
             </div>

           </div>
            <div class="card" v-for="(employer, index) in application.employers" :key="employer.id">
                <div class="card-header">Employer {{index+1}} of {{application.employers.length}}</div>
                <div class="card-body">
                    <div v-if="corrections.employerInfoCorrections[index].reasons || hasFlaggedEmployerInfoFields[index]" class="alert-danger p-2 mb-2">
                        <div v-if="corrections.employerInfoCorrections[index].reasons">
                            <b-row>
                                <b-col class="font-weight-bold">
                                    MDH admin who rejected your application has provided the following reasons regarding this employer:
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    {{corrections.employerInfoCorrections[index].reasons}}
                                </b-col>
                            </b-row>
                        </div>
                        <b-row v-if="hasFlaggedEmployerInfoFields[index]">
                            <b-col class="font-weight-bold">
                                Fields with issues have been highlighted in red.
                            </b-col>
                        </b-row>
                    </div>
                  <b-row>
                    <b-col>
                      <BaseRadioInput label="This facility is in Minnesota."
                                      :options="opts.yesNoBoolOptions"
                                      v-model="employer.inState"
                                      :v="$v.application.employers.$each[index].inState"
                                      @input="handleInstateUpdate(employer, $event)"
                      />
                    </b-col>
                  </b-row>
                    <b-row>
                        <b-col v-bind:class="corrections.employerInfoCorrections[index].staffingAgency?'text-danger':''">
                            <BaseCheckboxInput label="I am working at this facility through a staffing agency."
                                               v-model="employer.staffingAgency"
                                               :v="$v.application.employers.$each[index].staffingAgency"
                                               slug="staffingAgency"
                                               :setId="'staffingAgency'+index"
                                               @input="firstEmployerStaffingAgencyCheckboxUpdateCatch[index]?corrections.employerInfoCorrections[index].staffingAgency=false:firstEmployerStaffingAgencyCheckboxUpdateCatch[index]=true"
                            />
                        </b-col>
                    </b-row>
                  <b-row>
                    <b-col v-bind:class="corrections.employerInfoCorrections[index].facilityType?'is-invalid':''">
                      <BaseSelector v-model="employer.facilityType"
                                    :v="$v.application.employers.$each[index].facilityType"
                                    slug="facType"
                                    label="Facility Type"
                                    :setId="'facType'+index"
                                    :options="opts.facilityTypeOptions"
                                    @input="handleFacilityTypeUpdate(index)"
                      />
                    </b-col>
                  </b-row>
                    <b-row>
                        <b-col :cols="employer.inState&&(employer.facilityType==='NH'||employer.facilityType==='CBH')?10:12" v-bind:class="corrections.employerInfoCorrections[index].name?'is-invalid':''">
                            <BaseTextInput v-model="employer.name"
                                           :v="$v.application.employers.$each[index].name"
                                           slug="employerName"
                                           label="Facility Name"
                                           :setId="'name'+index"
                                           @input="corrections.employerInfoCorrections[index].name=false"
                            />
                        </b-col>
                      <b-col cols="2" v-if="employer.inState&&(employer.facilityType==='NH'||employer.facilityType==='CBH')">
                        <b-button variant="primary" @click="findFacilityByName(employer)">Search</b-button>
                      </b-col>
                    </b-row>
                  <b-row v-if="employer.searchResults">
                    <b-col>
                      <b-table hover small show-empty class="sr-caption" style="width: 100%"
                               aria-live="polite"
                               responsive="true"
                               :fields="employerSearchFields"
                               :items="employer.searchResults"
                               caption="Facility search table. To sort the data in this table by a specific column, go to the appropriate column header cell and use enter to sort the data ascending. Use enter again to sort descending.">
                        <tempate v-slot:no-data>
                          No Nursing Homes or Boarding Care Homes matching this name found.
                        </tempate>
                        <template v-slot:cell(actions)="row">
                          <b-button variant="primary" @click="selectFacility(row, employer)">Select</b-button>
                        </template>
                      </b-table>
                    </b-col>
                  </b-row>
                  <b-row>
                        <b-col cols="6" v-bind:class="corrections.employerInfoCorrections[index].department?'is-invalid':''">
                            <BaseTextInput v-model="employer.department"
                                           :v="$v.application.employers.$each[index].department"
                                           slug="employerDept"
                                           label="Department"
                                           :setId="'department'+index"
                                           @input="corrections.employerInfoCorrections[index].department=false"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col v-bind:class="corrections.employerInfoCorrections[index].address.streetAddress?'is-invalid':''">
                            <BaseTextInput v-model="employer.address.streetAddress"
                                           :v="$v.application.employers.$each[index].address.streetAddress"
                                           slug="streetAddress"
                                           label="Street Address"
                                           :setId="'streetAddress'+index"
                                           tip="Please include apartment number or PO Box number if relevant."
                                           @input="corrections.employerInfoCorrections[index].address.streetAddress=false"
                                           :plaintext="employer.staffReport && employer.inState && (employer.facilityType==='NH' || employer.facilityType==='CBH')"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="4" v-bind:class="corrections.employerInfoCorrections[index].address.city?'is-invalid':''">
                            <BaseTextInput v-model="employer.address.city"
                                           :v="$v.application.employers.$each[index].address.city"
                                           slug="city"
                                           label="City"
                                           :setId="'city'+index"
                                           @input="corrections.employerInfoCorrections[index].address.city=false"
                                           :plaintext="employer.staffReport && employer.inState && (employer.facilityType==='NH' || employer.facilityType==='CBH')"
                            />
                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.employerInfoCorrections[index].address.state?'is-invalid':''">
                            <BaseSelector v-model="employer.address.state"
                                          :v="$v.application.employers.$each[index].address.state"
                                          slug="state"
                                          label="State"
                                          :setId="'state'+index"
                                          :options="employer.inState?opts.stateOptions:opts.stateOptions.filter(s=>s.value!=='MN')"
                                          @input="firstEmployerStateSelectorUpdateCatch[index]?corrections.employerInfoCorrections[index].address.state=false:firstEmployerStateSelectorUpdateCatch[index]=true"
                                          :plaintext="employer.staffReport && employer.inState && (employer.facilityType==='NH' || employer.facilityType==='CBH')"
                            />

                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.employerInfoCorrections[index].address.postalCode?'is-invalid':''">
                            <BaseMaskedTextInput v-model="employer.address.postalCode"
                                                 :v="$v.application.employers.$each[index].address.postalCode"
                                                 slug="zip"
                                                 label="Zip Code"
                                                 :setId="'zip'+index"
                                                 mask="#####-####"
                                                 @input="corrections.employerInfoCorrections[index].address.postalCode=false"
                                                 :plaintext="employer.staffReport && employer.inState && (employer.facilityType==='NH' || employer.facilityType==='CBH')"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="6" v-bind:class="corrections.employerInfoCorrections[index].startDate?'is-invalid':''">
                            <BaseDateOfBirthInput v-model="employer.startDate"
                                           :v="$v.application.employers.$each[index].startDate"
                                           slug="startDate"
                                           label="Start Date"
                                           :setId="'startDate'+index"
                                           :messages="opts.startDateMessages"
                                           tip="Format: MM/DD/YYYY"
                                           @input="handleStartDateInput(index)"
                            />
                        </b-col>
                        <b-col cols="6" v-bind:class="corrections.employerInfoCorrections[index].endDate?'is-invalid':''">
                            <BaseDateOfBirthInput v-model="employer.endDate"
                                           :v="$v.application.employers.$each[index].endDate"
                                           slug="endDate"
                                           label="End Date"
                                           :setId="'endDate'+index"
                                           :messages="opts.endDateMessages"
                                           tip="Format: MM/DD/YYYY"
                                           @input="handleEndDateInput(index)"
                                           :plaintext="employer.verified && employer.inState && (employer.facilityType==='NH' || employer.facilityType==='CBH') && employer.verified.code==='VERIFIED'"
                            />
                            <b-button variant="secondary" v-if="!(employer.verified && employer.inState && (employer.facilityType==='NH' || employer.facilityType==='CBH') && employer.verified.code==='VERIFIED')" @click="clearEndDate(index)">
                                Clear End Date
                            </b-button>
                        </b-col>
                </b-row>
                  <div v-if="overTwoYears">
                    <div v-if="needsFirstPaystub(employer)" v-bind:class="[attachmentMarked(employer, index, 'PS', 'First')?'text-danger':'']">
                      <b-row>
                        <b-col cols="3">
                          <span>Current attached first paystub: </span>
                          <span>{{findAttachmentFileName(employer, 'PS', 'First')}}</span>
                          <span class="green-required"/>
                        </b-col>
                        <b-col>
                          <b-button variant="secondary" size="sm" @click="downloadAttachment(employer, 'PS','First')">Download</b-button>
                        </b-col>
                      </b-row>
                      <div v-if="missingAttachment(employer, index, 'PS','First')" class="d-block invalid-feedback">First paystub is required.</div>
                    </div>
                    <div v-for="n in calcNumberW2(employer)" v-bind:key="n">
                      <b-row>
                        <b-col cols="3">
                          <span v-bind:class="[attachmentMarked(employer, index, 'W2',calcW2Year(n,employer))?'text-danger':'']">
                          <span>Current attached W-2 for year {{calcW2Year(n,employer)}}: </span>
                          <span>{{findAttachmentFileName(employer, 'W2',calcW2Year(n, employer))}}</span>
                          <span class="green-required"/></span>
                        </b-col>
                        <b-col>
                          <b-button variant="secondary" size="sm" @click="downloadAttachment(employer, 'W2',calcW2Year)">Download</b-button>
                        </b-col>
                      </b-row>
                      <div v-if="missingAttachment(employer, index, 'W2',calcW2Year(n, employer))" class="d-block invalid-feedback">W-2 for year {{calcW2Year(n,employer)}} is required.</div>
                    </div>
                    <div v-if="needsLastPaystub(employer)" v-bind:class="[attachmentMarked(employer, index,'PS','Last')?'text-danger':'']">
                      <b-row>
                        <b-col cols="3">
                          <span>Current attached last paystub: </span>
                          <span>{{findAttachmentFileName(employer,'PS', 'Last')}}</span>
                          <span class="green-required"/>
                        </b-col>
                        <b-col>
                          <b-button variant="secondary" size="sm" @click="downloadAttachment(employer, 'PS','Last')">Download</b-button>
                        </b-col>
                      </b-row>
                      <div v-if="missingAttachment(employer, index, 'PS', 'Last')" class="d-block invalid-feedback">Last paystub is required.</div>
                    </div>
                  </div>
                  <div v-else v-bind:class="[attachmentMarked(employer,index,'PS')?'text-danger':'']">
                    <b-row>
                      <b-col cols="3">
                        <span>Current attached paystub: </span>
                        <span>{{findAttachmentFileName(employer,'PS')}}</span>
                        <span class="green-required"/>
                      </b-col>
                        <b-col>
                          <b-button variant="secondary" @click="downloadAttachment(employer, 'PS')">Download</b-button>
                      </b-col>
                    </b-row>
                    <div v-if="missingAttachment(employer, index, 'PS', 'Last')" class="d-block invalid-feedback">Paystub is required.</div>
                  </div>
                  <div v-if="employer.facilityType==='OTH'" v-bind:class="[attachmentMarked(employer,index,'JD')]">
                    <b-row>
                      <b-col cols="3">
                        <span>Current attached job description: </span>
                        <span>{{findAttachmentFileName(employer, 'JD')}}</span>
                        <span class="green-required"/>
                      </b-col>
                      <b-col>
                        <b-button variant="secondary" size="sm" @click="downloadAttachment(employer, 'JD')">Download</b-button>
                      </b-col>
                    </b-row>
                    <div v-if="missingAttachment(employer, index, 'JD')" class="d-block invalid-feedback">Job description is required.</div>
                  </div>
                    <b-row>
                        <b-col>
                            <FileAttachment v-on:attachment-added="addAttachment(index, $event)"
                                            v-on:attachment-failed="showRootToast('Error', 'Adding attachment failed','danger')"
                                            :attachment-type-options="trimAttachmentTypeOptions(employer)"
                                            describedBy="paystubs"
                                            :id="'paystub'+index"
                                            :startYear="getW2StartYear(employer)"
                                            :numYears="calcNumberW2(employer)"
                                            :needsFirstPaystub="needsFirstPaystub(employer)"
                                            :needsLastPaystub="needsLastPaystub(employer)"
                                            :overTwoYears="overTwoYears"
                            />
                        </b-col>
                    </b-row>
                    <b-row>

                        <b-col>
                            <b-button variant="secondary" @click="removeEmployer(index)" :disabled="application.employers.length<2">Remove Employer</b-button>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <div>
                <b-row>
                    <b-col>
                        <b-button variant="secondary" @click="addEmployer">Add Employer</b-button>
                    </b-col>
                </b-row>
            </div>
            <div class="card" v-if="application.external">
                <div class="card-header">Messages to/from MDH</div>
                <div class="card-body">
                    <b-row class="mt-2">
                        <b-col cols="12">
                            <b-table hover small show-empty class="sr-caption"
                                     aria-live="polite"
                                     responsive="true"
                                     :fields="noteFields"
                                     :items="application.external"
                                     :busy="false"
                                     label-sort-asc=""
                                     label-sort-desc=""
                                     :tbody-tr-class="highlightUnread"
                                     caption="Messages sent to/from MDH table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                                <template v-slot:no-data>
                                    No Messages Found.
                                </template>
                                <div slot="table-busy" class="text-center text-danger my-2">
                                    <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                                <template slot="empty" slot-scope="scope">
                                    No Messages Found.
                                </template>
                                <template v-slot:cell(noteType)="row">
                                    <div v-if="row.item && row.item.noteType.code==='EXT'">From MDH</div>
                                    <div v-else-if="row.item && row.item.noteType.code==='EXTIN'">To MDH</div>
                                    <div v-else>{{row.item.noteType.description}}</div>
                                </template>
                                <template v-slot:cell(noteDate)="row">
                                    {{formatNoteDateTime(row.item.noteDate)}}
                                </template>
                                <template v-slot:cell(noteByName)="row">
                                    <div v-if="row.item && row.item.noteType.code==='EXT'">MDH Staff</div>
                                    <div v-else>{{row.item.noteByName}}</div>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <b-row>
                <b-col>
                    <BaseParagraphInput v-model="comments" :v="$v.comments" label="Additional Comments" tip="If you wish to explain anything regarding your application you can do so here. Your response is optional and will be attached to your application."/>
                </b-col>
            </b-row>

            <div class="card">
                <div class="card-header">Applicant Affirmation</div>
                <div class="card-body">
                    <b-row>
                        <b-col>
                            The information I have provided in this application is true and accurate to the best of my knowledge.
                            I have read and will comply with the requirements of Minnesota Statutes, Chapter 146B. I understand that knowingly making a
                            false statement on this application could be cause for denial, suspension or revocation of certification. I understand by signing
                            this document, I give MDH the authority to contact any listed supervisor, employer and client submitted for use in
                            verification of credentials.
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12" lg="6">
                            <BaseTextInput v-model="signature"
                                           :v="$v.signature"
                                           slug="signature"
                                           label="Signature"
                                           setId="sig"/>
                        </b-col>
                        <b-col cols="12" lg="6">
                            <BaseDateInput v-model="dateSigned"
                                           :v="$v.dateSigned"
                                           label="Date"
                                           slug="dateSigned"
                                           :disabled="true"
                                           setId="dtSig"/>
                        </b-col>
                    </b-row>
                </div>
            </div>

            <b-row>
                <b-col>
                    <b-button variant="primary" @click="submit">
                        Resubmit Application
                    </b-button>
                </b-col>
                <b-col>
                    <b-button variant="secondary" @click="returnToView">
                        Cancel
                    </b-button>
                </b-col>
            </b-row>

            <b-modal id="name-modal" hide-header hide-footer>
                <h2>Update Name</h2>
                <hr/>
                <form ref="form">
                    <NameUpdate class="mb-2" v-on:attachment-complete="closeNameModal"/>
                    <b-button @click="closeNameModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                </form>
            </b-modal>
            <b-modal id="ssn-modal" hide-header hide-footer>
                <h2>Shown SSN is incorrect</h2>
                <hr/>
                <form ref="form">
                    <SsnUpdate class="mb-2" v-on:attachment-complete="closeSsnModal"/>
                    <b-button @click="closeSsnModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                </form>
            </b-modal>
        </b-container>
    </div>
</template>

<script>
    import axios from 'axios';
    import axiosRetry from 'axios-retry';
    import BaseTextInput from "@/components/templates/input/BaseTextInput";
    import BaseSelector from "@/components/templates/input/BaseSelector";
    import BaseTextOutput from "@/components/templates/input/BaseTextOutput";
    import BaseMaskedTextInput from "@/components/templates/input/BaseMaskedTextInput";
    import { required, email, maxLength, minLength } from 'vuelidate/lib/validators';
    import {commonOptions} from "@/mixins/commonOptions";
    import BaseCheckboxInput from "@/components/templates/input/BaseCheckboxInput";
    import minDate from "@/validators/minDate";
    import maxDate from "@/validators/maxDate";
    import BaseDateOfBirthInput from "@/components/templates/input/BaseDateOfBirthInput";
    import BaseDateInput from "@/components/templates/input/BaseDateInput";
    import FileAttachment from "@/components/templates/input/FileAttachment";
    import SsnUpdate from "@/views/cna-user/SsnUpdate";
    import NameUpdate from "@/views/cna-user/NameUpdate";
    import BaseParagraphInput from "@/components/templates/input/BaseParagraphInput";
    import moment from 'moment';
    import BaseRadioInput from "@/components/templates/input/BaseRadioInput.vue";

    axiosRetry(axios, { retries: 0,
        retryDelay: axiosRetry.exponentialDelay  })

    export default {
        name: "ResubmitRenewal",
        components:{
          BaseRadioInput,
            BaseTextInput,
            BaseTextOutput,
            BaseSelector,
            BaseMaskedTextInput,
            BaseCheckboxInput,
            BaseDateOfBirthInput,
            BaseDateInput,
            FileAttachment,
            SsnUpdate,
            NameUpdate,
            BaseParagraphInput,

        },
        data:()=>({
          employerSearchFields:[
            {key: "hfid", label: "HFID", sortable: true},
            {key: "name", label: "Name", sortable: true},
            {key: "address.streetAddress", label: "Address", sortable: true},
            {key: "address.city", label: "City", sortable: true},
            {key: "address.state", label: "State", sortable: true},
            {key: "address.postalCode", label: "Zip", sortable: true},
            {key: "actions", sortable: false}
          ],
            doneLoading: false,
            opts: {
                startDateMessages: {
                    minDate: 'Start Date must be after 01/01/1900.',
                    maxDate: 'Start Date cannot be in the future.'
                },
                endDateMessages: {
                    minDate: 'End Date must be after Start Date.',
                    maxDate: 'End Date can not be in the future.'
                },
                facilityTypeOptions: [],
                attachmentTypeOptions: []
            },
            lastWorkedDateFromPreviousApplication: null,
            overTwoYears: null,

            application: null,
            corrections: null,
            profile: null,
            comments: '',
            firstSelectorUpdateCatch: false,
            firstEmployerStateSelectorUpdateCatch: null,
            firstEmployerTypeSelectorUpdateCatch: null,
            firstEmployerStaffingAgencyCheckboxUpdateCatch: null,
            hasFlaggedEmployerInfoFields: [],
            hasFlaggedPersonalInfoFields: false,
            showFlaggedFieldsError: false,
            noteFields: [
                {key: 'note', label: 'Message',sortable:true},
                {key: 'noteType',label:'Message Type', sortable:true},
                {key: 'noteDate',label:'Message Date',sortable:true},
                {key:'noteByName',sortable:true,label:'Message By'},
            ],
            signature: null,
            dateSigned: new moment().format('YYYY-MM-DD'),

            gaps:[]
        }),
        created() {
            this.loadCorrections();
            this.loadFacilityTypeOptions();
            this.loadAttachmentTypeOptions();
            this.loadExpirationDate();
        },
        methods:{
            loadApplication() {
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/renewal/${this.$route.params.id}`)
                    .then(response => {
                        this.application = response.data.application;
                        for(let i=0;i<this.application.employers.length;i++){
                            this.application.employers[i].facilityType = this.application.employers[i].facilityType?this.application.employers[i].facilityType.code:null;
                            if(this.application.employers[i].staffingAgency==null){
                                this.application.employers[i].staffingAgency = false;
                            }
                            this.application.employers[i].inState = this.application.employers[i].address.state==='MN';
                            this.application.employers[i].searchResults = null;
                        }
                        this.profile = response.data.profile;
                        this.firstEmployerStateSelectorUpdateCatch = new Array(this.application.employers.length).fill(false);
                        this.firstEmployerTypeSelectorUpdateCatch = new Array(this.application.employers.length).fill(false);
                        this.firstEmployerStaffingAgencyCheckboxUpdateCatch = new Array(this.application.employers.length).fill(false);
                        this.$nextTick(()=>{
                            this.matchCorrectionsToEmployer();
                        });
                    })

            },
            loadCorrections(){
                axiosRetry(axios, {retries: 0});
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/corrections/${this.$route.params.id}`)
                    .then(response=>{
                        this.corrections = response.data;

                    })
                    .catch(error=> {
                        this.corrections = {
                            personalInfoCorrections: {
                                address: {
                                    streetAddress: null,
                                    city: null,
                                    state: null,
                                    postalCode: null
                                },
                                phone: null,
                                email: null,
                                reasons: null
                            },
                            employerInfoCorrections: []
                        }
                    })
                    .finally(()=>{
                        this.$nextTick(()=>{
                            this.loadApplication();
                        });
                    })
            },
            loadFacilityTypeOptions(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/facilityTypes`, {ignoreHeader: true})
                    .then(response => {
                        for(let type of response.data){
                            this.opts.facilityTypeOptions.push({
                                value: type.code,
                                text: type.description
                            })
                        }
                    })
            },
            loadAttachmentTypeOptions(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/employerTypes`, {ignoreHeader: true})
                    .then(response => {
                        for(let type of response.data){
                            this.opts.attachmentTypeOptions.push({
                                value: type.code,
                                text: type.description
                            })
                        }
                    });
            },
            loadExpirationDate(){
              axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile/user`)
                  .then(response =>{
                    this.lastWorkedDateFromPreviousApplication = moment(response.data.expirationDate).subtract(2,'y');
                    this.overTwoYears = moment(response.data.expirationDate).isBefore(moment());
                  });
            },
            matchCorrectionsToEmployer(){
                let temp = [];
                for(let i=0;i<this.application.employers.length;i++){
                    let correction = this.corrections.employerInfoCorrections.find(x=>x.id===this.application.employers[i].id);
                    if(correction){
                        temp.push(correction);
                    }
                    else{
                        temp.push({
                            id: this.application.employers[i].id,
                            show: null,
                            name: null,
                            department: null,
                            address: {
                                streetAddress: null,
                                city: null,
                                state: null,
                                postalCode: null
                            },
                            attachments: [],
                            reasons: null,
                            startDate: null,
                            endDate: null,
                            staffingAgency: null,
                            facilityType: null
                        })
                    }
                }
                this.corrections.employerInfoCorrections = temp;
                this.$nextTick(()=>{
                    this.calcFlaggedEmployerInfoFields();
                    this.calcFlaggedPersonalInfoFields();
                })

            },
            findAttachmentFileName(employer, type, otherType){
              let file;
              if(otherType){
                file=employer.attachments.find(x=>x.type.code === type && x.otherType.includes(''+otherType));
              }
              else {
                file = employer.attachments.find(x => x.type.code === type);
              }
                if(file){
                    return file.filename;
                }
                return "N/A"
            },
            downloadAttachment(employer, type){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/getUrl/` + employer.attachments.find(x=>x.type.code === type).attachmentId)
                    .then(response=>{
                        if(response.data){
                            window.open(response.data,"_blank");
                        }
                        else{this.$emit('attachment-get-failure')}
                    })
            },
            hasAttachmentType(employer, type){
                return employer.attachments.find(x=>x.type.code===type);
            },
          addAttachment(index, value){
            if(value.type.code==='W2'){
              let years = value.otherType.split(',');
              for(let i=0;i<years.length;i++){
                let previousW2ForYear = this.application.employers[index].attachments.find(a=>a.type.code==='W2' && a.otherType.includes(years[i]));
                if(previousW2ForYear){
                  let previousW2Years = previousW2ForYear.otherType.split(',').filter(y=>y!==years[i]);
                  if(previousW2Years.length===0){
                    this.application.employers[index].attachments = this.application.employers[index].attachments.filter(w2=>w2!==previousW2ForYear);
                    this.corrections.employerInfoCorrections[index].attachments = this.corrections.employerInfoCorrections[index].attachments.filter(a=>a.id!==previousW2ForYear.attachmentId);
                  }
                  else{
                    previousW2ForYear.otherType = previousW2Years.join(',');
                  }
                }
              }
              this.application.employers[index].attachments.push(value);
            }
            else {
              var existing = this.application.employers[index].attachments.findIndex(a => a.type.code === value.type.code && (a.otherType===null || value.otherType===a.otherType))
              if (existing > -1) {
                this.corrections.employerInfoCorrections[index].attachments = this.corrections.employerInfoCorrections[index].attachments.filter(a=>a.id!==this.application.employers[index].attachments[existing].attachmentId);
                this.application.employers[index].attachments[existing] = value
                this.application.employers.splice()
              } else {
                this.application.employers[index].attachments.push(value);
              }
            }
            this.showRootToast('Attachment added','Successfully added attachment','success');
          },
            removeAttachment(employer, type){
                employer.attachments = employer.attachments.filter(x=>x.type.code!==type);
            },
            trimAttachmentTypeOptions(employer){
                if(employer.facilityType==="OTH"){
                    return this.opts.attachmentTypeOptions;
                }
                return this.opts.attachmentTypeOptions.filter(a=>a.value!=="JD");
            },
            showRootToast(title, message, type) {
                this.$root.$bvToast.toast(message, {
                    title: title,
                    toaster: 'b-toaster-top-center',
                    solid: true,
                    variant: type,
                    appendToast: true,
                    noAutoHide:true
                })
                this.toastFocus()
            },
            toastFocus() {
                setTimeout(() => {
                    let elem = document.getElementsByClassName("toast")
                    elem = elem[elem.length-1]
                    elem.focus()
                }, 100)
            },
            clearEndDate(index){
                this.application.employers[index].endDate=null;
                this.corrections.employerInfoCorrections[index].endDate=false;
            },
            calcFlaggedPersonalInfoFields(){
                let temp = this.flattenObject(this.corrections.personalInfoCorrections);
                delete temp.reasons;
                let values = Object.values(temp);
                let temp2 = false;
                for(let i=0;i<values.length;i++){
                    if(values[i]){
                        temp2 = true;
                        break;
                    }
                }
                this.hasFlaggedPersonalInfoFields = temp2;
            },
            calcFlaggedEmployerInfoFields(){
                for(let i=0;i<this.corrections.employerInfoCorrections.length;i++) {
                    let temp = this.flattenObject(this.corrections.employerInfoCorrections[i]);
                    delete temp.id;
                    delete temp.reasons;
                    let values = Object.values(temp);
                    let temp2 = false;
                    for (let j = 0; j < values.length; j++) {
                        if (values[i]) {
                            temp2 = true;
                            break;
                        }
                    }
                    this.hasFlaggedEmployerInfoFields.push(temp2);
                }
                this.$nextTick(()=>{
                    this.doneLoading = true;
                })
            },
            flattenObject(obj){
                const flattened = {}
                Object.keys(obj).forEach((key) => {
                    const value = obj[key]
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        Object.assign(flattened, this.flattenObject(value))
                    } else {
                        flattened[key] = value
                    }
                })

                return flattened
            },
            addEmployer(){
                this.application.employers.push(
                    {
                        id: null,
                        name: null,
                        department: null,
                        address: {
                            streetAddress: null,
                            city: null,
                            postalCode: null,
                            state: null
                        },
                        startDate: null,
                        endDate: null,
                        attachments: [],
                        staffingAgency: null,
                        facilityType: '',
                        searchResults: null,
                        inState: true
                    });
                let employerInfoCorrection = {
                  id: null,
                  name: null,
                  department: null,
                  address:{
                    streetAddress: null,
                    city: null,
                    postalCode: null,
                    state: null
                  },
                  startDate: null,
                  endDate: null,
                  attachments: [],
                  staffingAgency: null,
                  facilityType: null
                }
                this.corrections.employerInfoCorrections.push(employerInfoCorrection);
                this.hasFlaggedEmployerInfoFields.push(false);
                this.firstEmployerStateSelectorUpdateCatch.push(true);
                this.firstEmployerTypeSelectorUpdateCatch.push(true);
                this.firstEmployerStaffingAgencyCheckboxUpdateCatch.push(true);
            },
            removeEmployer(index){
                this.$delete(this.application.employers, index);
                this.$delete(this.firstEmployerTypeSelectorUpdateCatch, index);
                this.$delete(this.firstEmployerStateSelectorUpdateCatch, index);
                this.$delete(this.firstEmployerStaffingAgencyCheckboxUpdateCatch, index);
                this.$delete(this.corrections.employerInfoCorrections, index);
                this.$delete(this.hasFlaggedEmployerInfoFields, index);
            },
            showNameModal() {
                this.$bvModal.show('name-modal')
            },
            closeNameModal() {
                this.$bvModal.hide('name-modal')
            },
            showSsnModal() {
                this.$bvModal.show('ssn-modal')
            },
            closeSsnModal() {
                this.$bvModal.hide('ssn-modal')
            },
            submit(){
                this.$v.$touch();
                if(this.$v.$invalid){
                    this.showRootToast('Error submitting application','Errors found in form','danger');
                    return;
                }
                if(this.checkAllFlaggedFields()){
                    this.showRootToast('Error submitting application','Some marked fields haven\'t been corrected','danger');
                    return;
                }
                let payload ={
                  firstName: this.profile.firstName,
                  lastName: this.profile.lastName,
                  middleName: this.profile.middleName,
                  ssn: this.profile.ssn,
                  dateOfBirth: this.profile.dateOfBirth,
                  employers: this.application.employers,

                };
                for(let i = 0; i<payload.employers.length;i++){
                  delete payload.employers[i].staffReport;
                }
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/renewal/resubmit/${this.$route.params.id}`, payload)
                    .then(response=>{
                        axios.post(`${process.env.VUE_APP_API_BASE_URL}/profile/contact/`,  this.profile.contact)
                        .then(response => {
                            this.showRootToast('Application Resubmitted','Your application was been successfully resubmitted','success');
                            if(this.comments){
                                axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/notes/${this.$route.params.id}/addFromUser`,this.comments)
                                    .catch(error=>{
                                        this.showRootToast('Error adding note','The comment you included with this application has failed to submit','danger');
                                    })
                            }
                            this.$nextTick(()=>{
                                this.$router.push({path: `/view-renewal/${this.$route.params.id}`})
                            })
                        })
                }).catch(error=>{
                    if(error.response.status===406){
                        if(error.response.data==='NoEdits'){
                            this.showRootToast('No edits applied','Application correction has been rejected because you have not changed anything. If you entered this page in error, please click on the cancel button on the bottom of the page to return to your application view.','warning');
                        }
                        else if(error.response.data==='InvalidStatus'){
                            this.showRootToast('Error submitting application','This application has received a final decision and is not eligible for corrections. Please submit a new renewal application.','danger');
                        }
                        else{
                            this.showRootToast('Error submitting application', 'The Application has failed to submit', 'danger');
                        }
                    }
                    else if(error.response.status===401){
                        this.showRootToast('Error submitting application','Your login into NAR has expired. Please refresh this page, log in again, and resubmit your corrections.','danger');
                    }
                    else if(error.response.status===500){
                        this.showRootToast('Error submitting application','The Application has failed to submit due to a server error. Please contact support at health.fpc-nar@state.mn.us.','danger');
                    }
                    else if(error.response.status===404){
                        this.showRootToast('Error submitting application', 'The Application not found.', 'danger');
                    }
                    else if(error.response.status===403){
                        this.showRootToast('Error submitting application', 'This application does not belong to you, please log in as the correct user and try again, or please press on Home button on top of the page to return to your dashboard.', 'danger');
                    }
                    else {
                        this.showRootToast('Error submitting application', 'The Application has failed to submit', 'danger');
                    }
                })

            },

            checkAllFlaggedFields(){
                let flattened = [];
                let temp = this.flattenObject(this.corrections.personalInfoCorrections);
                delete temp.reasons;
                flattened = flattened.concat(Object.values(temp));

                for(let i=0;i<this.corrections.employerInfoCorrections.length;i++){
                    temp = this.flattenObject(this.corrections.employerInfoCorrections[i]);
                    delete temp.id;
                    delete temp.reasons;
                    delete temp.show;
                    flattened = flattened.concat(Object.values(temp));
                }
                let composites = flattened.filter(x=> x!=null && x.length != null);
                flattened = flattened.filter(x=>x==null || x.length==null);
                for(let i =0;i<composites.length;i++){
                  if(composites[i].length && composites[i].length > 0 && composites[i][0]!=null && composites[i][0].mark!=null){
                    flattened.push(composites[i][0].mark);
                    }
                }
                for(let i=0;i<flattened.length;i++){
                    if(flattened[i]){
                        return true;
                    }
                }
                return false;
            },

            handleFacilityTypeUpdate(index){
                if(this.firstEmployerTypeSelectorUpdateCatch[index]){
                    this.corrections.employerInfoCorrections[index].facilityType=false;
                }
                else{
                    this.firstEmployerTypeSelectorUpdateCatch[index]=true;
                }
                if(this.application.employers[index].facilityType!=='OTH'){
                    this.corrections.employerInfoCorrections[index].attachments.jobDescription = false;
                }
            },
            missingAttachment(employer, index, attachmentType, otherType){
              let attachment;
              if(otherType) {
                attachment = employer.attachments.find(a=>a.type.code === attachmentType
                    && (a.otherType != null && a.otherType.includes(''+otherType)));
              }
              else{
                attachment = employer.attachments.find(a=>a.type.code === attachmentType);
              }
                return this.$v.application.employers.$each[index].$dirty && this.$v.application.employers.$each[index].$invalid && attachment==null
            },
            highlightUnread(item,type){
                return (item != null && item.noteType.code==='EXT' && item.noteRead.code==="UNREAD") ? 'table-warning' : '';
            },
            formatNoteDateTime(noteDateTime){
                return moment(noteDateTime).format('MM/DD/YYYY hh:mmA');
            },
            returnToView(){
                this.$router.push({path: `/view-renewal/${this.$route.params.id}`})
            },
          findFacilityByName(emp){
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/facility/find`, emp.name)
                .then(response=>{
                  emp.searchResults = response.data;
                })
                .catch(error=>{
                  this.showRootToast("Not found", "Unable to find a nursing home or boarding care facility matching this name", "danger")
                })
                .finally(()=>{
                  emp.name = '';
                  emp.address.streetAddress = '';
                  emp.address.city = '';
                  emp.address.state = 'MN';
                  emp.address.postalCode = '';
                  emp.startDate = null;
                  emp.endDate = null;
                })
          },
          selectFacility(row, emp){
            emp.name=row.item.name;
            emp.address.streetAddress=row.item.address.streetAddress;
            emp.address.city=row.item.address.city;
            emp.address.state=row.item.address.state;
            emp.address.postalCode=row.item.address.postalCode;
            emp.hfid=row.item.hfid;
            if(row.item.staffReport){
              emp.staffReport = row.item.staffReport;
              emp.lastWorkedDate=row.item.staffReport.lastWorkedDate;
            }
            emp.searchResults = null;
          },
          handleInstateUpdate(emp, event){
              if(event){
                emp.address.state='MN';
              }
              else{
                emp.address.state='';
              }
          },
          needsFirstPaystub(emp){
            if(!this.overTwoYears){
              return false;
            }
            let dateStarted = moment(emp.startDate);
            let yearStarted = dateStarted.year();
            let earliestEmployerStartingSameYear = this.application.employers.filter(e=>e!==emp&&moment(e.startDate).year()===yearStarted&&dateStarted.isAfter(moment(e.startDate)));
            if(earliestEmployerStartingSameYear.length>0){
              return false;
            }
            let previousLastWorkedYear = this.lastWorkedDateFromPreviousApplication.year();
            if(yearStarted-previousLastWorkedYear<2){
              return false;
            }
            let previousYear = yearStarted-1;
            let employersForPreviousYear = this.application.employers.filter(e=> e.endDate!=null && previousYear<=moment(e.endDate).year() && previousYear>=moment(e.startDate).year());
            if(employersForPreviousYear.length>0){
              return false;
            }
            previousYear--;
            employersForPreviousYear = this.application.employers.filter(e=> e.endDate!=null && previousYear<=moment(e.endDate).year() && previousYear>=moment(e.startDate).year());
            return employersForPreviousYear.length > 0 || previousYear === previousLastWorkedYear;
          },
          needsLastPaystub(emp){
            if(!this.overTwoYears){
              return false;
            }
            if(emp.endDate==null){
              return true;
            }

            let yearEnded = moment(emp.endDate).year();
            let currentYear = moment().year();
            if(yearEnded === currentYear){
              return true;
            }
            if(currentYear-yearEnded<2){
              return false;
            }
            let nextYear = yearEnded+1;
            let employersForNextYear = this.application.employers.filter(e=>e!==emp && nextYear>=moment(e.startDate).year() && (e.endDate==null || nextYear<=moment(e.endDate).year()));
            if(employersForNextYear.length>0){
              return false;
            }
            nextYear++;
            employersForNextYear = this.application.employers.filter(e=> nextYear>=moment(e.startDate).year() && (e.endDate==null || nextYear<=moment(e.endDate).year()));
            return employersForNextYear.length > 0 || nextYear === currentYear;
          },
          turnNegativeValueToZero(n){
            return n<0?0:n;
          },
          calcEmploymentGaps(){
            let gaps = [];
            let employersWithStartDates = this.application.employers.filter(e=>e.startDate!=null);
            let daysNotWorked = Array(moment().diff(this.lastWorkedDateFromPreviousApplication,'d')+1).fill(true);
            for(let i=0;i<employersWithStartDates.length;i++){
              let startIndex = this.turnNegativeValueToZero(moment(employersWithStartDates[i].startDate).diff(this.lastWorkedDateFromPreviousApplication,'d'));
              let endIndex;
              if(employersWithStartDates[i].endDate==null){
                endIndex = moment().diff(this.lastWorkedDateFromPreviousApplication,'d')
              }
              else{
                endIndex = moment(employersWithStartDates[i].endDate).diff(this.lastWorkedDateFromPreviousApplication,'d');
              }
              daysNotWorked.fill(false,startIndex,endIndex+1);
            }
            let i=0;
            while(i<daysNotWorked.length){
              if(daysNotWorked[i]){
                let j=i+1;
                while(daysNotWorked[j]){
                  j++;
                }
                let gapStart = moment(this.lastWorkedDateFromPreviousApplication);
                gapStart.add(i,'d');
                let gapEnd = moment(this.lastWorkedDateFromPreviousApplication);
                gapEnd.add(j-1,'d');
                if(gapEnd.diff(gapStart,'y',true)>=2){
                  gaps.push({
                    start: gapStart.format("MM/DD/yyyy"),
                    end: gapEnd.format("MM/DD/yyyy")
                  })
                }
                i=j;
              }
              else{
                i++;
              }
            }
            this.gaps=gaps;
          },
          calcNumberW2(employer){
            let employerStartDate = moment(employer.startDate);
            let employerEndDate = employer.endDate?moment(employer.endDate):moment();

            let result = (employerEndDate.year() - (employerStartDate.isAfter(this.lastWorkedDateFromPreviousApplication)?employerStartDate.year():this.lastWorkedDateFromPreviousApplication.year()) + 1);
            if(this.needsLastPaystub(employer)){
              result--;
            }
            if(this.needsFirstPaystub(employer)){
              result--;
            }
            return this.turnNegativeValueToZero(result);
          },
          calcW2Year(n, employer){
            let employerStartDate = moment(employer.startDate);
            let result = (n + (employerStartDate.isAfter(this.lastWorkedDateFromPreviousApplication)?employerStartDate.year():this.lastWorkedDateFromPreviousApplication.year()) - 1);
            if(this.needsFirstPaystub(employer)){
              result++;
            }
            return result;
          },
          getW2StartYear(employer){
            if(!employer.startDate){
              return null;
            }
            let employerStartDate = moment(employer.startDate);
            let result = (employerStartDate.isAfter(this.lastWorkedDateFromPreviousApplication)?employerStartDate.year():this.lastWorkedDateFromPreviousApplication.year());
            if(this.needsFirstPaystub(employer)){
              result++;
            }
            return result;
          },
          attachmentMarked(employer, index, type, otherType){
              let attachment;
              if(otherType){
                attachment = employer.attachments.find(x=>x.type.code===type && x.otherType.includes(otherType));
              }
              else{
                attachment = employer.attachments.find(x=>x.type.code===type);
              }
              if(attachment){
                let attachmentMark = this.corrections.employerInfoCorrections[index].attachments.find(x=>x.id===attachment.attachmentId);
                return attachmentMark?attachmentMark.mark:false;
              }
              return false;
          },
          handleStartDateInput(index){
            this.corrections.employerInfoCorrections[index].startDate=false;
            this.calcEmploymentGaps();
          },
          handleEndDateInput(index){
            this.corrections.employerInfoCorrections[index].endDate=false;
            this.calcEmploymentGaps();
          }
        },
        validations(){
            return{
                profile:{
                    required: true,
                    contact:{
                        required: true,
                        phone: {required},
                        email: {required, email},
                        address:{
                            required: true,
                            streetAddress: {required},
                            city: {required},
                            state: {required},
                            postalCode:{
                                required,
                                minLength: minLength(5),
                                maxLength: maxLength(10)
                            },
                            applicationId: {required: false}
                        }
                    }
                },
                application:{
                    required,
                    employers:{
                        required,
                        $each:{
                            id: {required: false},
                            name: {required},
                            department: {required:false},
                            address:{
                                streetAddress: {required},
                                city: {required},
                                postalCode: {required},
                                state: {required}
                            },
                            startDate:{
                                required,
                                minDate: minDate(new Date('1900-01-01')),
                                maxDate: maxDate(new Date())
                            },
                            endDate:{
                                required: false,
                                minDate: minDate(">"),
                                maxDate: maxDate(new Date())
                            },
                            attachments:{
                                required,
                                correctAttachments: function(obj, parent){
                                  if(this.overTwoYears) {
                                    if (this.needsFirstPaystub(parent)) {
                                      if (!obj.find(a => a.type.code === 'PS' && a.otherType === 'First')) {
                                        return false;
                                      }
                                    }
                                    if (this.needsLastPaystub(parent)) {
                                      if (!obj.find(a => a.type.code === 'PS' && a.otherType === 'Last')) {
                                        return false;
                                      }
                                    }
                                    let w2StartYear = this.getW2StartYear(parent);
                                    let numW2 = this.calcNumberW2(parent);
                                    for (let i = 0; i < numW2; i++) {
                                      if (!obj.find(a => a.type.code === 'W2' && a.otherType.includes((w2StartYear + i) + ''))) {
                                        return false;
                                      }
                                    }
                                  }
                                  else{
                                    if(!obj.find(a=>a.type.code==='PS')){
                                      return false;
                                    }
                                  }
                                  if(parent.facilityType.code==='OTH'){
                                    if(!obj.find(a=>a.type.code==='JD')){
                                      return false;
                                    }
                                  }
                                  return true;
                                },
                                minLength: minLength(1)
                            },
                            staffingAgency: {required: false},
                            facilityType: {
                                required
                            },
                            inState: {required: false},
                        }
                    },
                },
                comments: {required: false},
                signature: {required},
                dateSigned: {required}
            }
        },
        mixins: [
            commonOptions
        ]
    }
</script>

<style scoped>

</style>